import classnames from 'classnames';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Icon, IconTypes } from '@eika/icon';
import { Input } from '@eika/input';
import { ErrorLabel } from '@eika/label';

import { useConfig } from '../../../defaultConfig';
import { KidFormat } from '../../../generated-types';
import { useNoteMaxLengthWithKIDSelected } from '../../../hooks/useNoteMaxLength';
import useTranslation, { format } from '../../../hooks/useTranslation';
import TranslationsConfigType, { NoteTypes } from '../../../translations/translationType';
import { getNoteType } from '../../NoteField/NoteField';
import { SelectedReceiver } from '../../transaction/SelectedReceiver';
import ToggleMessageInput from './ToggleMessageInput';

import './MessageInput.scss';

export interface MessageInputProps {
    className?: string;
    noteValue?: string;
    kidValue?: string;
    placeholder?: string;
    label?: string;
    error?: string;
    onChange?: (value: string) => void;
    onChangeKid?: (value: string) => void;
    selectedReceiver?: SelectedReceiver;
    locked?: boolean;
    disableKid?: boolean;
    isLoading?: boolean;
    isKidOnly?: boolean;
    maxLength?: number;
}

export const findDefaultNoteType = (
    kidFormats: KidFormat[] | undefined,
    noteType: NoteTypes,
    kidValue: string | undefined,
    noteValue: string | undefined,
) => {
    if ((kidValue && kidFormats?.length) || noteType === NoteTypes.KID) return NoteTypes.KID;
    if (noteType === NoteTypes.MESSAGE || noteValue) return NoteTypes.MESSAGE;
    return NoteTypes.KID;
};

const findNotePlaceholder = (
    noteType: NoteTypes,
    placeholder: string | undefined,
    activeNoteType: NoteTypes | undefined,
    translated: TranslationsConfigType,
) => {
    if (placeholder) return placeholder;
    if (activeNoteType) return translated.amountNote[activeNoteType].placeholder;
    return translated.amountNote[noteType].placeholder;
};
const MessageInput: FC<MessageInputProps> = ({
    className,
    noteValue,
    kidValue,
    placeholder,
    label,
    error,
    onChange,
    onChangeKid,
    selectedReceiver,
    locked,
    disableKid = false,
    isLoading = false,
    isKidOnly = false,
    maxLength,
}) => {
    const {
        featureToggles: { enableKidValidation },
    } = useConfig();
    const translated = useTranslation();

    const noteType = useMemo(() => {
        if (isLoading) {
            if (isKidOnly) return NoteTypes.KID;
            return NoteTypes.MESSAGE;
        }
        return getNoteType(selectedReceiver, disableKid, isKidOnly);
    }, [isLoading, selectedReceiver, disableKid, isKidOnly]);
    const defaultNoteType = useMemo(
        () => findDefaultNoteType(selectedReceiver?.kidFormats, noteType, kidValue, noteValue),
        [selectedReceiver?.kidFormats, noteType, kidValue, noteValue],
    );

    const [activeNoteType, setActiveNoteType] = useState<NoteTypes>(defaultNoteType);
    const showToggle =
        (noteType === NoteTypes.KID_OR_MESSAGE && !locked && selectedReceiver && !disableKid) ||
        (!selectedReceiver && !locked && !isKidOnly && !disableKid);
    const noteText = label ?? translated.amountNote[activeNoteType].label;
    const notePlaceholder = findNotePlaceholder(noteType, placeholder, activeNoteType, translated);

    const [localError, setLocalError] = useState<string>();

    const noteMaxLength = useNoteMaxLengthWithKIDSelected({ selectedReceiver });
    const selectedMaxLength = maxLength || noteMaxLength;

    useEffect(() => {
        setLocalError(undefined);
    }, [setLocalError, selectedReceiver]);

    useEffect(() => {
        setActiveNoteType(defaultNoteType);
    }, [defaultNoteType, selectedReceiver]);

    const onChangeEvent = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const targetValue = e.target.value;
            setLocalError(undefined);
            const snipped =
                targetValue.length >= selectedMaxLength ? targetValue.substring(0, selectedMaxLength) : targetValue;

            const formattedValue = isKidOnly || noteType === NoteTypes.KID ? snipped.replace(/[^0-9-]/g, '') : snipped;
            if (targetValue.length > selectedMaxLength) {
                setLocalError(format(translated.tooManyCharacters, selectedMaxLength));
            }
            if (
                targetValue.length > selectedMaxLength &&
                (isKidOnly || noteType === NoteTypes.KID) &&
                enableKidValidation
            ) {
                setLocalError(format(translated.tooManyCharactersKID, selectedMaxLength));
            }
            if (activeNoteType === NoteTypes.KID) {
                if (onChangeKid) onChangeKid(formattedValue);
            } else if (onChange) onChange(formattedValue);
        },
        [
            selectedMaxLength,
            isKidOnly,
            noteType,
            enableKidValidation,
            activeNoteType,
            translated.tooManyCharacters,
            translated.tooManyCharactersKID,
            onChangeKid,
            onChange,
        ],
    );
    const value = activeNoteType === NoteTypes.KID ? kidValue : noteValue;

    const onChangeActiveNoteType = () => {
        if (activeNoteType === NoteTypes.MESSAGE) {
            setActiveNoteType(NoteTypes.KID);
            if (onChange) onChange('');
        }
        if (activeNoteType === NoteTypes.KID) {
            setActiveNoteType(NoteTypes.MESSAGE);
            if (onChangeKid) onChangeKid('');
        }
    };

    return (
        <section
            role="tabpanel"
            aria-labelledby="active-toggle-label"
            className={classnames('message-field', { error }, className)}
        >
            <span className="message-field__label">
                {showToggle ? (
                    <ToggleMessageInput
                        activeNoteType={activeNoteType}
                        onChangeActiveNoteType={onChangeActiveNoteType}
                    />
                ) : (
                    <span
                        id="active-toggle-label"
                        className="message-label-text"
                        data-testid={activeNoteType === NoteTypes.MESSAGE ? `message-active` : `KID-active`}
                    >
                        {noteText}
                    </span>
                )}
                {locked && (
                    <span className="payment-padlock-icon">
                        <Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" />
                    </span>
                )}
            </span>
            <Input
                type="text"
                disabled={locked}
                maxLength={selectedMaxLength + 1}
                value={value ?? ''}
                placeholder={isLoading || locked ? '' : notePlaceholder}
                onChange={onChangeEvent}
                data-testid="kid-message-input"
            />
            {(localError || error) && <ErrorLabel icon={IconTypes.UTROPSTEGN_24} text={localError ?? error} />}
        </section>
    );
};

export default MessageInput;
